import { useEffect, useRef, useState } from "react";
import {
  cardStyle,
  sectionTitleStyle,
  emptyBoxStyle,
  colors,
} from "../ui/styles";

import API, { authFetch } from "../services/api";

function DashboardPage({ onOpenPlant }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [notificationFilter, setNotificationFilter] = useState("all");
  const notificationsRef = useRef(null);

  const monitoringWindowDays = 30;

  const utenteCorrente = JSON.parse(localStorage.getItem("utenteCorrente"));
  const role = utenteCorrente?.role || "viewer";

  const formatDate = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString("it-IT");
  };

  const caricaDashboard = async () => {
    try {
      setLoading(true);

      const [dashboardRes, notificationsRes] = await Promise.all([
        authFetch(`${API}/dashboard/summary`),
        authFetch(`${API}/dashboard/notifications`),
      ]);

      if (!dashboardRes.ok) {
        throw new Error("Errore caricamento dashboard");
      }

      const dashboardData = await dashboardRes.json();
      const notificationsData = notificationsRes.ok
        ? await notificationsRes.json()
        : [];

      setDashboard(dashboardData);
      setNotifications(notificationsData);
    } catch (err) {
      console.error(err);
      alert("Errore caricamento dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    caricaDashboard();
  }, []);

  if (loading) return <p>Caricamento dashboard...</p>;
  if (!dashboard) return <p>Nessun dato dashboard disponibile.</p>;

  const kpi = dashboard.kpi || {};

  const badgeStyle = (type) => {
    const styles = {
      ok: { background: "#dcfce7", color: "#166534" },
      warning: { background: "#fef3c7", color: "#92400e" },
      danger: { background: "#fee2e2", color: "#991b1b" },
      neutral: { background: "#e2e8f0", color: "#334155" },
      info: { background: "#dbeafe", color: "#1e40af" },
    };

    return {
      ...styles[type],
      display: "inline-flex",
      alignItems: "center",
      padding: "5px 10px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 700,
      whiteSpace: "nowrap",
    };
  };

  const filteredNotifications = notifications.filter((notification) => {
    if (notificationFilter === "all") return true;

    if (notificationFilter === "critical") {
      return notification.priority === "critical";
    }

    if (notificationFilter === "monitor") {
      return notification.priority !== "critical";
    }

    if (notificationFilter === "maintenance") {
      return notification.type?.toLowerCase().includes("maintenance");
    }

    if (notificationFilter === "deadline") {
      return notification.type?.toLowerCase().includes("deadline");
    }

    if (notificationFilter === "maintenance_warning") {
      return notification.type === "maintenance_warning";
    }

    if (notificationFilter === "maintenance_critical") {
      return notification.type === "maintenance_critical";
    }

    if (notificationFilter === "deadline_warning") {
      return notification.type === "deadline_warning";
    }

    if (notificationFilter === "deadline_critical") {
      return notification.type === "deadline_critical";
    }

    return true;
  });

  const getAccentColor = (type, value) => {
    if (type === "danger" && Number(value) > 0) return "#dc2626";
    if (type === "warning" && Number(value) > 0) return "#d97706";
    if (type === "ok") return "#16a34a";
    if (type === "info") return "#2563eb";
    return colors.primary;
  };

  const openPlant = (plantId) => {
    if (!plantId || !onOpenPlant) return;
    onOpenPlant(plantId);
  };

  const scrollToNotifications = (filter) => {
    setNotificationFilter(filter);

    setTimeout(() => {
      notificationsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);
  };

  const isFilterButtonActive = (value) => {
    if (notificationFilter === value) return true;

    if (
      value === "maintenance" &&
      notificationFilter.startsWith("maintenance_")
    ) {
      return true;
    }

    if (value === "deadline" && notificationFilter.startsWith("deadline_")) {
      return true;
    }

    return false;
  };

  const kpiCard = ({
    label,
    value,
    subtitle,
    explanation,
    type = "neutral",
    onClick,
    actionText,
  }) => {
    const accentColor = getAccentColor(type, value);
    const isClickable = Boolean(onClick);

    return (
      <div
        onClick={onClick}
        onMouseEnter={(e) => {
          if (!isClickable) return;
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow =
            "0 6px 14px rgba(15,60,117,0.12)";
          e.currentTarget.style.borderColor = "#93c5fd";
        }}
        onMouseLeave={(e) => {
          if (!isClickable) return;
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow =
            "0 2px 8px rgba(15,60,117,0.06)";
          e.currentTarget.style.borderColor = "#dbe4f0";
        }}
        style={{
          ...cardStyle,
          background: "#f8fbff",
          borderLeft: `5px solid ${accentColor}`,
          minHeight: "118px",
          cursor: isClickable ? "pointer" : "default",
          transition: "all 0.2s ease",
        }}
      >
        <div
          style={{
            color: colors.lightText,
            fontSize: "14px",
            marginBottom: "8px",
          }}
        >
          {label}
        </div>

        <div
          style={{
            color: accentColor,
            fontSize: "36px",
            fontWeight: 800,
            lineHeight: 1,
          }}
        >
          {value ?? 0}
        </div>

        {subtitle && (
          <div
            style={{
              color: colors.text,
              fontSize: "13px",
              marginTop: "10px",
              lineHeight: "1.4",
              fontWeight: 600,
            }}
          >
            {subtitle}
          </div>
        )}

        {explanation && (
          <div
            style={{
              color: colors.muted,
              fontSize: "12px",
              marginTop: "6px",
              lineHeight: "1.4",
            }}
          >
            {explanation}
          </div>
        )}

        {actionText && (
          <div
            style={{
              marginTop: "10px",
              color: "#1e40af",
              fontSize: "12px",
              fontWeight: 700,
            }}
          >
            {actionText}
          </div>
        )}
      </div>
    );
  };

  const plantButton = (plantId) =>
    plantId ? (
      <button
        className="btn btn-secondary"
        onClick={(e) => {
          e.stopPropagation();
          openPlant(plantId);
        }}
        style={{
          background: "#e2e8f0",
          border: "none",
          borderRadius: "8px",
          padding: "8px 12px",
          cursor: "pointer",
          fontWeight: 600,
          marginTop: "10px",
        }}
      >
        Apri impianto
      </button>
    ) : null;

  const listCard = ({ title, subtitle, items, emptyText, renderItem }) => {
    const hasManyItems = items?.length > 4;

    return (
      <div style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <div>
            <h2 style={sectionTitleStyle}>{title}</h2>

            {subtitle && (
              <p
                style={{
                  color: colors.muted,
                  fontSize: "13px",
                  marginTop: "6px",
                  marginBottom: 0,
                  lineHeight: "1.4",
                }}
              >
                {subtitle}
              </p>
            )}
          </div>

          <span style={badgeStyle(items?.length > 0 ? "neutral" : "ok")}>
            {items?.length || 0}
          </span>
        </div>

        {items?.length === 0 ? (
          <div
            style={{
              ...emptyBoxStyle,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "#166534",
              background: "#f8fbff",
            }}
          >
            <span
              style={{
                width: "22px",
                height: "22px",
                borderRadius: "999px",
                background: "#dcfce7",
                color: "#166534",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: "13px",
              }}
            >
              ✓
            </span>
            <span>{emptyText}</span>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              maxHeight: hasManyItems ? "520px" : "none",
              overflowY: hasManyItems ? "auto" : "visible",
              paddingRight: hasManyItems ? "6px" : 0,
            }}
          >
            {items.map(renderItem)}
          </div>
        )}
      </div>
    );
  };

  const renderAgendaItem = (item) => (
    <div key={item.id} style={emptyBoxStyle}>
      <span style={badgeStyle(item.type === "deadline" ? "danger" : "warning")}>
        {item.type === "deadline" ? "Verifica tecnica" : "Manutenzione"}
      </span>

      <div style={{ marginTop: "10px" }}>
        <strong>{item.title || "Attività operativa"}</strong>
      </div>

      <div style={{ marginTop: "6px" }}>
        <strong>Data:</strong> {formatDate(item.date)}
      </div>

      <div>
        <strong>Stato:</strong> {item.status || "—"}
      </div>

      <div>
        <strong>Impianto:</strong> #{item.plant_id || "—"}
      </div>

      {plantButton(item.plant_id)}

      <div style={{ marginTop: "8px" }}>
        <strong>Note:</strong> {item.notes || "—"}
      </div>
    </div>
  );

  return (
    <>
      <div style={{ marginBottom: "26px" }}>
        <h1
          style={{
            color: colors.title,
            fontSize: "32px",
            fontWeight: 400,
            marginBottom: "8px",
          }}
        >
          Dashboard
        </h1>

        <p style={{ color: colors.muted, fontSize: "16px" }}>
          Monitoraggio operativo impianti, attività tecniche e conformità
        </p>

        <div style={{ marginTop: "10px" }}>
          <span style={badgeStyle("info")}>
            Ruolo:{" "}
            {role === "admin"
              ? "Amministratore"
              : role === "technician"
              ? "Tecnico"
              : "Supervisore"}
          </span>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: "18px",
          marginBottom: "24px",
        }}
      >
        {kpiCard({
          label: "Manutenzioni imminenti",
          value: kpi.maintenance_due_soon,
          subtitle: `Previste entro ${monitoringWindowDays} giorni`,
          explanation: "Attività da pianificare o confermare.",
          type: "warning",
          actionText: "Visualizza manutenzioni →",
          onClick: () => scrollToNotifications("maintenance_warning"),
        })}

        {kpiCard({
          label: "Manutenzioni in ritardo",
          value: kpi.maintenance_overdue,
          subtitle: "Oltre la data prevista",
          explanation: "Richiedono una verifica operativa.",
          type: "danger",
          actionText: "Visualizza criticità →",
          onClick: () => scrollToNotifications("maintenance_critical"),
        })}

        {kpiCard({
          label: "Verifiche imminenti",
          value: kpi.technical_deadlines_due_soon,
          subtitle: `In scadenza entro ${monitoringWindowDays} giorni`,
          explanation: "Controlli tecnici e adempimenti prossimi.",
          type: "warning",
          actionText: "Visualizza verifiche →",
          onClick: () => scrollToNotifications("deadline_warning"),
        })}

        {kpiCard({
          label: "Verifiche in ritardo",
          value: kpi.technical_deadlines_overdue,
          subtitle: "Oltre la data prevista",
          explanation: "Priorità alta per conformità e sicurezza.",
          type: "danger",
          actionText: "Visualizza criticità →",
          onClick: () => scrollToNotifications("deadline_critical"),
        })}
      </div>

      <div
        ref={notificationsRef}
        style={{
          ...cardStyle,
          marginBottom: "24px",
          background: "#f8fbff",
          border: "1px solid #dbe4f0",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "16px",
            marginBottom: "16px",
          }}
        >
          <div>
            <h2 style={sectionTitleStyle}>Centro notifiche operative</h2>

<p
  style={{
    color: colors.muted,
    fontSize: "14px",
    marginTop: "6px",
    marginBottom: "6px",
  }}
>
  Avvisi automatici generati dal sistema in base a manutenzioni,
  verifiche tecniche e attività da monitorare.
</p>

<div
  style={{
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "10px",
    color: "#1e40af",
    fontSize: "13px",
    marginTop: "4px",
  }}
>
  ℹ️ Le notifiche rappresentano gli eventi generati nel periodo monitorato e
  possono essere superiori agli indicatori operativi attuali.
</div>
          </div>

          <span
            style={badgeStyle(filteredNotifications.length > 0 ? "warning" : "ok")}
          >
            {notificationFilter === "all"
  ? `${notifications.length} notifiche attive • periodo monitorato ${monitoringWindowDays} giorni`
  : `${filteredNotifications.length} di ${notifications.length} notifiche attive • periodo monitorato ${monitoringWindowDays} giorni`}
          </span>

          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              marginBottom: "16px",
            }}
          >
            {[
              ["all", "Tutte"],
              ["critical", "Critiche"],
              ["monitor", "Da monitorare"],
              ["maintenance", "Manutenzioni"],
              ["deadline", "Verifiche"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setNotificationFilter(value)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(15,60,117,0.10)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
                style={{
                  border: "1px solid #dbe4f0",
                  borderRadius: "999px",
                  padding: "7px 12px",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "12px",
                  background: isFilterButtonActive(value)
                    ? "#dbeafe"
                    : "#ffffff",
                  color: isFilterButtonActive(value) ? "#1e40af" : "#334155",
                  transition: "all 0.2s ease",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {filteredNotifications.length === 0 ? (
          <div style={emptyBoxStyle}>
            Nessuna notifica operativa. Non risultano attività urgenti o
            imminenti.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: "14px",
              maxHeight: filteredNotifications.length > 6 ? "760px" : "none",
              overflowY: filteredNotifications.length > 6 ? "auto" : "visible",
              paddingRight: filteredNotifications.length > 6 ? "6px" : 0,
            }}
          >
            {filteredNotifications.map((notification, index) => (
              <div
                key={`${notification.type}-${notification.plant_id}-${index}`}
                onClick={() => openPlant(notification.plant_id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 14px rgba(15,60,117,0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
                style={{
                  ...emptyBoxStyle,
                  background:
                    notification.priority === "critical"
                      ? "#fff1f2"
                      : "#fffbeb",
                  border:
                    notification.priority === "critical"
                      ? "1px solid #fecdd3"
                      : "1px solid #fde68a",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                <span
                  style={badgeStyle(
                    notification.priority === "critical"
                      ? "danger"
                      : "warning"
                  )}
                >
                  {notification.priority === "critical"
                    ? "Critica"
                    : "Da monitorare"}
                </span>

                <div
                  style={{
                    marginTop: "10px",
                    color: colors.title,
                    fontWeight: 800,
                    fontSize: "15px",
                  }}
                >
                  {notification.title}
                </div>

                <div
                  style={{
                    color: colors.muted,
                    fontSize: "14px",
                    lineHeight: "1.5",
                    marginTop: "6px",
                  }}
                >
                  {notification.message}
                </div>

                <div style={{ marginTop: "8px", fontSize: "13px" }}>
                  <strong>Data:</strong>{" "}
                  {formatDate(notification.event_date || notification.date)}
                </div>

                <div style={{ marginTop: "4px", fontSize: "13px" }}>
                  <strong>Impianto:</strong>{" "}
                  {notification.plant
                    ? `${notification.plant.plant_type} ${notification.plant.model}`
                    : `#${notification.plant_id}`}
                </div>

                {notification.plant?.brand && (
                  <div style={{ marginTop: "4px", fontSize: "13px" }}>
                    <strong>Marca:</strong> {notification.plant.brand}
                  </div>
                )}

                {notification.plant?.serial_number && (
                  <div style={{ marginTop: "4px", fontSize: "13px" }}>
                    <strong>Matricola:</strong>{" "}
                    {notification.plant.serial_number}
                  </div>
                )}

                {plantButton(notification.plant_id)}
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: "18px",
          marginBottom: "26px",
        }}
      >
        {kpiCard({
          label: "Clienti gestiti",
          value: kpi.customers,
          subtitle: "Anagrafiche operative",
          explanation: "Clienti registrati nel gestionale.",
        })}

        {kpiCard({
          label: "Impianti attivi",
          value: kpi.plants,
          subtitle: "Parco impianti censito",
          explanation: "Impianti collegati ai clienti.",
        })}

        {kpiCard({
          label: "Interventi tecnici",
          value: kpi.interventions,
          subtitle: "Attività registrate",
          explanation: "Storico degli interventi effettuati.",
          type: "ok",
        })}

        {kpiCard({
          label: "Documentazione",
          value: kpi.documents,
          subtitle: "Documenti caricati",
          explanation: "Libretti, rapporti e certificazioni.",
        })}
      </div>

      <div
        style={{
          ...cardStyle,
          marginBottom: "30px",
          background: "#f8fbff",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "16px",
            marginBottom: "18px",
          }}
        >
          <div>
            <h2 style={sectionTitleStyle}>Agenda operativa</h2>

            <p
              style={{
                color: colors.muted,
                fontSize: "14px",
                marginTop: "6px",
                marginBottom: 0,
                lineHeight: "1.5",
              }}
            >
              Attività ordinate per priorità temporale: oggi, domani e prossimi
              7 giorni.
            </p>
          </div>

          <span style={badgeStyle("info")}>Vista giornaliera</span>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: "18px",
          }}
        >
          {listCard({
            title: "Oggi",
            subtitle: "Attività da gestire nella giornata corrente.",
            items: dashboard.agenda?.today || [],
            emptyText: "Nessuna attività prevista per oggi.",
            renderItem: renderAgendaItem,
          })}

          {listCard({
            title: "Domani",
            subtitle: "Attività previste per il giorno successivo.",
            items: dashboard.agenda?.tomorrow || [],
            emptyText: "Nessuna attività prevista per domani.",
            renderItem: renderAgendaItem,
          })}

          {listCard({
            title: "Prossimi 7 giorni",
            subtitle: "Attività operative imminenti da pianificare.",
            items: dashboard.agenda?.week || [],
            emptyText: "Nessuna attività nei prossimi 7 giorni.",
            renderItem: renderAgendaItem,
          })}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: "22px",
        }}
      >
        {listCard({
          title: "Ultimi interventi tecnici",
          subtitle: "Le attività più recenti registrate sugli impianti.",
          items: dashboard.recent_interventions || [],
          emptyText: "Nessun intervento tecnico recente.",
          renderItem: (item) => (
            <div key={item.id} style={emptyBoxStyle}>
              <strong>{item.intervention_type || "Intervento tecnico"}</strong>

              <div style={{ marginTop: "6px" }}>
                <strong>Data:</strong> {formatDate(item.intervention_date)}
              </div>

              <div>
                <strong>Tecnico:</strong> {item.technician_name || "—"}
              </div>

              <div>
                <strong>Stato:</strong>{" "}
                <span style={badgeStyle("ok")}>{item.status || "—"}</span>
              </div>

              {plantButton(item.plant_id)}
            </div>
          ),
        })}

        {listCard({
          title: "Manutenzioni programmate",
          subtitle: `Le prossime attività manutentive, ordinate per data. Focus operativo: ${monitoringWindowDays} giorni.`,
          items: dashboard.upcoming_maintenance || [],
          emptyText: "Nessuna manutenzione programmata.",
          renderItem: (item) => (
            <div key={item.id} style={emptyBoxStyle}>
              <strong>{item.title || "Manutenzione programmata"}</strong>

              <div style={{ marginTop: "6px" }}>
                <strong>Data prevista:</strong>{" "}
                {formatDate(item.next_due_date)}
              </div>

              <div>
                <strong>Stato:</strong>{" "}
                <span style={badgeStyle("warning")}>{item.status || "—"}</span>
              </div>

              <div>
                <strong>Note:</strong> {item.notes || "—"}
              </div>

              {plantButton(item.plant_id)}
            </div>
          ),
        })}

        {listCard({
          title: "Verifiche tecniche",
          subtitle: `Controlli, adempimenti e verifiche operative da monitorare entro ${monitoringWindowDays} giorni.`,
          items: dashboard.upcoming_deadlines || [],
          emptyText: "Nessuna verifica tecnica da monitorare.",
          renderItem: (item) => (
            <div key={item.id} style={emptyBoxStyle}>
              <strong>{item.title || "Verifica tecnica"}</strong>

              <div style={{ marginTop: "6px" }}>
                <strong>Data prevista:</strong> {formatDate(item.due_date)}
              </div>

              <div>
                <strong>Stato:</strong>{" "}
                <span style={badgeStyle("warning")}>{item.status || "—"}</span>
              </div>

              <div>
                <strong>Note:</strong> {item.notes || "—"}
              </div>

              {plantButton(item.plant_id)}
            </div>
          ),
        })}
      </div>
    </>
  );
}

export default DashboardPage;